import { useState } from "react";

import {
  useAddTenantUserMutation,
  useRemoveTenantUserMutation,
  useTenantUsersQuery,
  useUpdateTenantUserMutation,
  type AddTenantUserPayload,
} from "@/api/tenants";
import { ApiError } from "@/api/client";
import { useAuth } from "@/auth";
import { Button, Ribbon, SectionHeader } from "@/components/ui";
import { AddUserForm } from "@/features/kullanicilar/AddUserForm";
import { MembersTable } from "@/features/kullanicilar/MembersTable";
import { PendingList } from "@/features/kullanicilar/PendingList";
import { t } from "@/i18n";

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message) return error.message;
  return fallback;
}

/** /kullanicilar — uye/rol/onay yonetimi (gorev talimati madde 4). */
export function Kullanicilar() {
  const { principal } = useAuth();
  const tenantId = principal?.active_tenant?.id ?? 0;

  const usersQuery = useTenantUsersQuery(tenantId);
  const addMutation = useAddTenantUserMutation(tenantId);
  const updateMutation = useUpdateTenantUserMutation(tenantId);
  const removeMutation = useRemoveTenantUserMutation(tenantId);

  const [generatedPassword, setGeneratedPassword] = useState<{ email: string; password: string } | null>(null);
  const [copyLabel, setCopyLabel] = useState<string | null>(null);

  const users = usersQuery.data ?? [];
  const pending = users.filter((u) => u.status === "pending");
  const others = users.filter((u) => u.status !== "pending");

  function handleAdd(payload: AddTenantUserPayload) {
    setGeneratedPassword(null);
    addMutation.mutate(payload, {
      onSuccess: (res) => {
        if (res.password) setGeneratedPassword({ email: res.email, password: res.password });
      },
    });
  }

  function handleApprove(userId: number, role: "tenant_admin" | "tenant_user") {
    updateMutation.mutate({ userId, payload: { status: "active", role } });
  }

  function handleReject(userId: number, reason: string) {
    updateMutation.mutate({
      userId,
      payload: reason ? { status: "rejected", rejection_reason: reason } : { status: "rejected" },
    });
  }

  function handleChangeRole(userId: number, role: "tenant_admin" | "tenant_user") {
    updateMutation.mutate({ userId, payload: { role } });
  }

  async function handleCopyPassword() {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword.password);
      setCopyLabel(t("kullanicilar.add.copied"));
    } catch {
      // Panoya erisim yoksa (izin/HTTPS/jsdom) sessizce yut — sifre zaten
      // ekranda gorunur durumda, kullanici elle secip kopyalayabilir.
    }
  }

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <h1 className="font-display text-xl font-bold text-ink">{t("kullanicilar.title")}</h1>

      {/* INCELEME BULGUSU: onayla/reddet/rol degistir (updateMutation) hatalari
          (409 son-yonetici korumasi rol degisikliginde de tetiklenebilir —
          tenants.py set_membership/LastAdminError) hicbir yerde
          GOSTERILMIYORDU; addMutation/removeMutation'in aksine sessizce
          yutuluyordu. Ayni mutasyon PendingList VE MembersTable'in rol
          secimi tarafindan paylasildigindan tek bir sayfa-seviyesi serit. */}
      {updateMutation.isError && <Ribbon tone="bad">{errorMessage(updateMutation.error, t("common.error"))}</Ribbon>}

      {generatedPassword && (
        <div className="card space-y-2 border-amber bg-amber-pale">
          <p className="text-sm font-semibold text-ink">{t("kullanicilar.add.password_title")}</p>
          <p className="text-xs text-ink">{t("kullanicilar.add.password_warning")}</p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-[9px] bg-card px-2 py-1 text-sm">{generatedPassword.password}</code>
            <Button type="button" variant="outline" size="sm" onClick={() => void handleCopyPassword()}>
              {copyLabel ?? t("kullanicilar.add.copy")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setGeneratedPassword(null);
                setCopyLabel(null);
              }}
            >
              {t("kullanicilar.add.close")}
            </Button>
          </div>
        </div>
      )}

      <AddUserForm
        onSubmit={handleAdd}
        isSubmitting={addMutation.isPending}
        error={addMutation.isError ? errorMessage(addMutation.error, t("common.error")) : null}
      />

      <div className="space-y-2">
        <SectionHeader title={t("kullanicilar.pending.title")} />
        <PendingList pending={pending} onApprove={handleApprove} onReject={handleReject} />
      </div>

      <div className="space-y-2">
        <SectionHeader title={t("kullanicilar.list.title")} />
        {usersQuery.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
        {usersQuery.isError && <Ribbon tone="bad">{t("common.error")}</Ribbon>}
        {!usersQuery.isLoading && !usersQuery.isError && (
          <MembersTable
            members={others}
            onChangeRole={handleChangeRole}
            onRemove={(userId) => removeMutation.mutate(userId)}
            removeError={removeMutation.isError ? errorMessage(removeMutation.error, t("common.error")) : null}
            removingUserId={removeMutation.isPending ? (removeMutation.variables ?? null) : null}
          />
        )}
      </div>
    </section>
  );
}
